import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormField, form, required, submit } from '@angular/forms/signals';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MeasureUnitResponse } from '@tourfold/libraries/tf-api-client-public-v2';
import { SignalFormServerErrors } from '@tourfold/libraries/shared-util';

export interface MeasureUnitFormResult {
  readonly name: string;
  readonly symbol: string;
}

interface MeasureUnitFormData {
  name: string;
  symbol: string;
}

const EMPTY_FORM: MeasureUnitFormData = { name: '', symbol: '' };

/**
 * Create/edit dialog for a measure unit (name + symbol). Built with Angular Signal Forms; the
 * parent list component performs the API call and feeds RFC 9457 errors back through
 * {@link applyServerError} so field-level violations render inline next to the offending input.
 */
@Component({
  selector: 'tf-settings-measure-unit-dialog',
  imports: [FormField, FormsModule, TranslateModule, ButtonModule, DialogModule, InputTextModule],
  templateUrl: './measure-unit-dialog.html',
  styleUrl: './measure-unit-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasureUnitDialog {
  readonly visible = input.required<boolean>();
  readonly unit = input<MeasureUnitResponse | null>(null);
  readonly isSaving = input(false);

  readonly save = output<MeasureUnitFormResult>();
  readonly cancelled = output<void>();
  readonly visibilityChange = output<boolean>();

  readonly formModel = signal<MeasureUnitFormData>({ ...EMPTY_FORM });

  readonly measureUnitForm = form(this.formModel, (schema) => {
    required(schema.name, { message: 'SETTINGS_MEASURE_UNITS.NAME_REQUIRED' });
    required(schema.symbol, { message: 'SETTINGS_MEASURE_UNITS.SYMBOL_REQUIRED' });
  });

  readonly serverErrors = new SignalFormServerErrors(this.formModel, ['name', 'symbol']);

  /** General (non-field) error shown as a banner inside the dialog, e.g. a uniqueness conflict. */
  readonly generalError = signal<string | null>(null);

  private readonly translate = inject(TranslateService);

  readonly isEditMode = computed(() => this.unit() !== null);

  readonly dialogTitle = computed(() =>
    this.isEditMode()
      ? 'SETTINGS_MEASURE_UNITS.EDIT_TITLE'
      : 'SETTINGS_MEASURE_UNITS.CREATE_TITLE'
  );

  constructor() {
    effect(() => {
      const editTarget = this.unit();
      if (!this.visible()) return;

      this.formModel.set(
        editTarget
          ? { name: editTarget.name ?? '', symbol: editTarget.symbol ?? '' }
          : { ...EMPTY_FORM }
      );
      this.serverErrors.clear();
      this.generalError.set(null);
    });
  }

  onSave(): void {
    this.generalError.set(null);
    submit(this.measureUnitForm, async () => {
      const { name, symbol } = this.formModel();
      this.save.emit({ name: name.trim(), symbol: symbol.trim() });
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onVisibleChange(visible: boolean): void {
    this.visibilityChange.emit(visible);
    if (!visible) {
      this.onCancel();
    }
  }

  /**
   * Apply a server-side error response to the form. Called by the parent on API failure. Field
   * violations (RFC 9457 with a property pointer) render inline next to the input; anything else
   * — a general message or, failing that, a generic create/update fallback — is shown in the
   * dialog's error banner so the user always sees why the save was rejected without leaving the
   * dialog. Returns the general messages for the caller's reference.
   */
  async applyServerError(error: unknown): Promise<readonly string[]> {
    const applied = await this.serverErrors.apply(error);
    const general = applied?.generalMessages ?? [];
    if (general.length > 0) {
      this.generalError.set(general.join(' '));
    } else if ((applied?.fieldCount ?? 0) === 0) {
      this.generalError.set(
        this.translate.instant(
          this.isEditMode()
            ? 'SETTINGS_MEASURE_UNITS.ERROR.UPDATE_FAILED'
            : 'SETTINGS_MEASURE_UNITS.ERROR.CREATE_FAILED'
        )
      );
    } else {
      this.generalError.set(null);
    }
    return general;
  }
}
